<template>
  <div class="project-tab-attributes-plans">
    <!-- Global Project Detail -->
    <v-card class="mb-7">
      <v-card-title class="d-flex align-center">
        <span>Project Detail</span>
        <v-icon v-if="hasExperts" color="success" class="ms-2">
          {{ icons.mdiShieldCheckOutline }}
        </v-icon>
        <v-spacer></v-spacer>
        <v-btn
          v-if="$can('add', 'project')"
          :color="statusColor"
          :disabled="!projectDataLocal.editable"
          @click="isProjectAttributesDialogOpen = true"
        >
          Edit
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          
          <v-col cols="12" md="6">
            <div class="mb-4">
              <div class="text-body-1 mb-1">
                <span class="font-weight-bold me-1">Departments involved:</span>
                <span class="text--secondary">{{ departments }}</span>
              </div>
              <div class="text-body-2 text--secondary">Total sample size: N={{ projectDataLocal.sample }}</div>
            </div>

            <div class="mb-4">
              <div class="font-weight-bold">
                Report Due: <u class="primary--text">{{ formatDate(projectDataLocal.report_date) }}</u>
              </div>
              <div class="text-caption grey--text">
                Closed Won: {{ formatDate(projectDataLocal.closed_won_date) }}
              </div>
            </div>

            <div class="d-flex align-center">
              <avatar :userList="projectDataLocal.insights" label="Insights" size="40" class="me-4" />
              <avatar v-if="projectDataLocal.experts" :userList="projectDataLocal.experts" label="Experts" size="40" />
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <v-alert :color="statusColor" text class="mb-4">
              <div class="font-weight-bold mb-1">Fielding in: {{ fieldCountries }}</div>
              <div class="text-sm">{{ formatDate(projectDataLocal.field_start) }} — {{ formatDate(projectDataLocal.field_end) }}</div>
            </v-alert>

            <div class="d-flex justify-space-between font-weight-bold text-caption mb-1">
              <span>Field Progress</span>
              <span>{{ projectDataLocal.days_started }} / {{ projectDataLocal.days_in_field }} Days</span>
            </div>
            <v-progress-linear
              :value="projectDataLocal.field_progress"
              :color="statusColor"
              height="10"
              rounded
              striped
            ></v-progress-linear>
            <div class="text-caption mt-1 text-right italic">{{ projectDataLocal.days_remaining }} days remaining</div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <div class="text-subtitle-1 font-weight-bold mb-3">Project Attributes</div>
        <v-row dense>
          <v-col v-for="(attr, i) in globalAttributes" :key="i" cols="12" sm="6" lg="3">
            <div class="text-caption grey--text font-weight-bold text-uppercase">{{ attr.label }}</div>
            <div class="text-body-1 mb-3">{{ attr.value }}</div>
          </v-col>
          <v-col cols="12">
            <div class="text-caption grey--text font-weight-bold text-uppercase">Comments</div>
            <div class="text-body-2">{{ projectDataLocal.key_comments || 'No comments' }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card 
      v-for="(country, index) in projectDataLocal.countries"
      :key="country.id"
      class="mb-7"
      :style="getCountryBorderStyle(country.status)"
    >
      <v-card-title>
        {{ country.country_detail.name }}
        <v-chip
          small label
          :color="resolveCountryStatusVariant(country.status)"
          :class="`v-chip-light-bg ${resolveCountryStatusVariant(country.status)}--text font-weight-bold ml-2`"
        >
          {{ resolveCountryStatusLabelVariant(country.status) }}
        </v-chip>
        
        <v-spacer></v-spacer>
        
        <v-btn
          v-if="$can('update', 'project')"
          small outlined
          :color="resolveCountryStatusVariant(country.status)"
          class="me-2"
          @click="openCountryEdit(index)"
        >
          Edit
        </v-btn>

        <v-menu bottom left v-if="$can('update', 'survey')">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on"><v-icon>{{ icons.mdiDotsVertical }}</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="getSurveyRoute(country)">
              <v-list-item-icon class="me-2"><v-icon small>{{ icons.mdiOrderBoolDescendingVariant }}</v-icon></v-list-item-icon>
              <v-list-item-title>Survey Builder</v-list-item-title>
            </v-list-item>
            <v-list-item @click="handlePackDescriptor(country)">
              <v-list-item-icon class="me-2">
                <v-icon small>{{ country.pack_descriptor ? icons.mdiImageText : icons.mdiImageAutoAdjust }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ country.pack_descriptor ? 'Pack Descriptor' : 'Create Descriptor' }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-timeline dense class="timeline-custom-dense">
              <v-timeline-item color="error" small>
                <div class="text-caption font-weight-bold">Field Start</div>
                <div>{{ formatDate(country.field_start_date) }}</div>
              </v-timeline-item>
              <v-timeline-item color="secondary" small>
                <div class="text-caption font-weight-bold">Research Location</div>
                <div>{{ country.research_location_detail.name }}</div>
              </v-timeline-item>
              <v-timeline-item color="accent" small>
                <div class="text-caption font-weight-bold">Studio Info</div>
                <div class="text-body-2">Shelf: {{ country.shelf_type_detail.name }}</div>
                <div class="text-body-2">Rate: {{ country.studio_rate_card }}</div>
              </v-timeline-item>
              <v-timeline-item color="warning" small>
                <div class="text-caption font-weight-bold">Composition</div>
                <div class="text-body-2">Size: N={{ country.sample }} | Phases: {{ country.phase }}</div>
                <div v-if="projectDataLocal.project_type.name === 'Qualitative'" class="text-body-2">
                   Qual: {{ arrayToString(country.qual_sample_type) }}
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-col>

          <v-col cols="12" md="6">
            <v-timeline dense class="timeline-custom-dense">
              <v-timeline-item color="info" small>
                <div class="text-caption font-weight-bold">Modules</div>
                <div class="text-body-2">{{ arrayToString(country.modules_detail) }}</div>
              </v-timeline-item>
              <v-timeline-item v-if="[1, 2, 5, 6].includes(country.research_location)" color="secondary" small>
                <div class="text-caption font-weight-bold">Field Locations</div>
                <div class="text-body-2">{{ arrayToString(country.field_locations, 'field_name') }}</div>
              </v-timeline-item>
              
              <v-timeline-item color="primary" small>
                <div class="text-caption font-weight-bold mb-2">Departments</div>
                <div class="d-flex flex-wrap">
                   <avatar 
                    v-for="dept in countryDepts" 
                    :key="dept.key"
                    :userList="country[dept.key]" 
                    :label="dept.label"
                    class="mb-2 me-2"
                   />
                </div>
              </v-timeline-item>

              <v-timeline-item color="success" small>
                <div class="text-caption font-weight-bold">Field End</div>
                <div>{{ formatDate(country.field_end_date) }}</div>
              </v-timeline-item>
            </v-timeline>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-btn
      v-if="$can('add','project')"
      :color="statusColor"
      @click="isCountryAddDialogOpen = true"
    >
      <v-icon left>{{ icons.mdiPlus }}</v-icon> Add New Market
    </v-btn>

    <project-attributes-edit :projectData="projectData" :isProjectAttributesDialogOpen.sync="isProjectAttributesDialogOpen" @update-project="updateProject" />
    <country-attributes-edit :projectData="projectData" :isCountryAttributesDialogOpen.sync="isCountryAttributesDialogOpen" :index="countryIndex" :csatResult="csatResult" @update-project="updateProject" />
    <country-add :projectData="projectData" :isCountryAddDialogOpen.sync="isCountryAddDialogOpen" @add-market="addMarket" />
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import { 
  mdiPlus, mdiDotsVertical, mdiOrderBoolDescendingVariant, 
  mdiImageText, mdiImageAutoAdjust, mdiShieldCheckOutline 
} from '@mdi/js'
import { formatDate } from '@core/utils/filter'
import useProjectsList from '../../project-list/useProjectsList'
import useProjectView from '../useProjectView'
import Avatar from '@/views/components/avatar/Avatar.vue'
import CountryAttributesEdit from './CountryAttributesEdit.vue'
import ProjectAttributesEdit from './ProjectAttributesEdit.vue'
import CountryAdd from './CountryAdd.vue'


export default {
  components: {
    Avatar,
    CountryAttributesEdit,
    ProjectAttributesEdit,
    CountryAdd
  },
  props: {
    projectData: { type: Object, required: true },
    csatResult: { type: Array, default: () => [] }
  },
  setup(props, { emit }) {
    const { resolveProjectStatusVariant } = useProjectsList()
    const { resolveCountryStatusVariant, resolveCountryStatusLabelVariant } = useProjectView()

    const projectDataLocal = ref(JSON.parse(JSON.stringify(props.projectData)))
    const isProjectAttributesDialogOpen = ref(false)
    const isCountryAttributesDialogOpen = ref(false)
    const isCountryAddDialogOpen = ref(false)
    const countryIndex = ref(0)

    // Configuration for department loop
    const countryDepts = [
      { key: 'field', label: 'Field' },
      { key: 'studio', label: 'Studio' },
      { key: 'programming', label: 'Prgm/DP/Coding' },
      { key: 'modeling', label: 'Modeling' },
      { key: 'project_management', label: 'Proj Mgmt' },
      { key: 'admin', label: 'Admin' },
      { key: 'logistic', label: 'Logistic' },
    ]

    const statusColor = computed(() => resolveProjectStatusVariant(projectDataLocal.value.job_status.name))
    const hasExperts = computed(() => projectDataLocal.value.experts?.length > 0)

    const departments = computed(() => arrayToString(projectDataLocal.value.departments))
    const researchMethods = computed(() => arrayToString(projectDataLocal.value.research_method))

    const globalAttributes = computed(() => [
      { label: 'Project Type', value: projectDataLocal.value.project_type.name },
      { label: 'Research Method', value: researchMethods.value },
      { label: 'Business Initiative', value: projectDataLocal.value.business_initiative.name },
      { label: 'Service Type', value: projectDataLocal.value.service_type.name },
    ])

    const fieldCountries = computed(() => {
      return projectDataLocal.value.countries
        .filter(c => c.status !== 'CLD')
        .map(c => c.country_detail.name)
        .join(", ")
    })

    const arrayToString = (array, key = 'name') => {
      return (array || []).map(item => item[key]).join(', ') || 'N/A'
    }

    const getCountryBorderStyle = (status) => {
      const color = resolveCountryStatusVariant(status)
      return `border: 2px solid var(--v-${color}-base) !important;`
    }

    const getSurveyRoute = (country) => {
      return country.questionnaire 
        ? { name: 'survey-project-view', params: { id: projectDataLocal.value.job_number, country: country.country_detail.code } }
        : { name: 'survey-view' }
    }

    const openCountryEdit = (idx) => {
      countryIndex.value = idx
      isCountryAttributesDialogOpen.value = true
    }

    const handlePackDescriptor = (country) => {
      if (country.pack_descriptor) {
        window.open(`https://webtools.prsiv.polynom.io/studies/${country.pack_descriptor}`, '_blank')
      } else {
        emit('add-pack-desc', country)
      }
    }

    const updateProject = (data) => emit('update-project', data)
    const addMarket = (data) => emit('add-market', data)

    watch(() => props.projectData, (newVal) => {
      projectDataLocal.value = JSON.parse(JSON.stringify(newVal))
    })

    return {
      projectDataLocal,
      isProjectAttributesDialogOpen, isCountryAttributesDialogOpen, isCountryAddDialogOpen,
      countryIndex,
      statusColor, hasExperts, departments, fieldCountries, globalAttributes, countryDepts,
      formatDate, arrayToString, getCountryBorderStyle, getSurveyRoute,
      resolveCountryStatusVariant, resolveCountryStatusLabelVariant, resolveProjectStatusVariant,
      updateProject, addMarket, openCountryEdit, handlePackDescriptor,
      icons: { mdiPlus, mdiDotsVertical, mdiOrderBoolDescendingVariant, mdiImageText, mdiImageAutoAdjust, mdiShieldCheckOutline }
    }
  }
}
</script>